import { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, setAuthToken, getAuthToken } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();

      if (token) {
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.user);
        } catch (err) {
          console.error('Failed to get current user:', err);
          // Token is invalid, clear it
          logout();
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  // Register function
  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authAPI.register(userData);

      // Save token and user data
      setAuthToken(response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);

      setLoading(false);
      return { success: true, data: response };
    } catch (err) {
      setError(err);
      setLoading(false);
      return { success: false, error: err };
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authAPI.login(email, password);

      // Save token and user data
      setAuthToken(response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);

      setLoading(false);
      return { success: true, data: response };
    } catch (err) {
      setError(err);
      setLoading(false);
      return { success: false, error: err };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout endpoint (optional, mainly for logging)
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local storage and state
      setAuthToken(null);
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  // Update profile function
  const updateProfile = async (userData) => {
    try {
      setError(null);
      const response = await authAPI.updateProfile(userData);

      // Update user data
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);

      return { success: true, data: response };
    } catch (err) {
      setError(err);
      return { success: false, error: err };
    }
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    updateProfile,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
